import { useState, type DragEvent } from 'react'
import { Plus, RefreshCw, Trash2 } from 'lucide-react'
import { SlidePreviewFrame } from './SlidePreviewFrame'
import { slideToCtx } from '../lib/slideCtx'
import type { SlideDraft } from '../types'

interface Props {
  slides: SlideDraft[]
  selectedId: string | null
  onSelect: (id: string) => void
  onAddClick: () => void
  onDelete: (id: string) => void
  onChangeTemplate: (id: string) => void
  onReorder: (draggedId: string, targetId: string | null) => void
}

export function Timeline({ slides, selectedId, onSelect, onAddClick, onDelete, onChangeTemplate, onReorder }: Props) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | 'end' | null>(null)

  const handleDeleteClick = (id: string) => {
    if (confirmingId === id) {
      onDelete(id)
      setConfirmingId(null)
    } else {
      setConfirmingId(id)
      setTimeout(() => setConfirmingId((cur) => (cur === id ? null : cur)), 3000)
    }
  }

  const onDragOverSlot = (e: DragEvent, id: string | 'end') => {
    e.preventDefault()
    if (draggedId) setOverId(id)
  }

  const onDropSlot = (e: DragEvent, id: string | 'end') => {
    e.preventDefault()
    if (draggedId) onReorder(draggedId, id === 'end' ? null : id)
    setDraggedId(null)
    setOverId(null)
  }

  return (
    <div className="timeline">
      {slides.map((slide) => (
        <div
          key={slide.id}
          className={`timeline-slot-wrap${slide.id === selectedId ? ' active' : ''}${slide.id === draggedId ? ' dragging' : ''}${overId === slide.id ? ' drop-target' : ''}`}
          draggable
          onDragStart={() => setDraggedId(slide.id)}
          onDragEnd={() => {
            setDraggedId(null)
            setOverId(null)
          }}
          onDragOver={(e) => onDragOverSlot(e, slide.id)}
          onDrop={(e) => onDropSlot(e, slide.id)}
        >
          <button type="button" className="timeline-slot" onClick={() => onSelect(slide.id)}>
            <SlidePreviewFrame templateId={slide.templateId} content={slide.content} ctx={slideToCtx(slide)} displayWidth={140} />
          </button>
          <div className="timeline-slot__toolbar">
            <button
              type="button"
              className="icon-button-ghost"
              title="Trocar template"
              onClick={(e) => {
                e.stopPropagation()
                onChangeTemplate(slide.id)
              }}
            >
              <RefreshCw size={13} />
            </button>
            <button
              type="button"
              className={`icon-button-ghost${confirmingId === slide.id ? ' danger' : ''}`}
              title={confirmingId === slide.id ? 'Clique de novo pra confirmar' : 'Apagar tela'}
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(slide.id)
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className={`timeline-add${overId === 'end' ? ' drop-target' : ''}`}
        onClick={onAddClick}
        onDragOver={(e) => onDragOverSlot(e, 'end')}
        onDrop={(e) => onDropSlot(e, 'end')}
        aria-label="Adicionar tela"
      >
        <Plus size={28} />
      </button>
    </div>
  )
}
